import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';
import { Chart, BarElement, Legend, LinearScale, Tooltip, CategoryScale } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import intlShape from '@/shared/utils/intlShape';

import { listItemMsg } from '@/shared/components/FormattedListValue';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import { currencyTooltipLabelWithInlt } from '@/shared/utils/chartjsHelpers';
import messages from '../dashboardMessages';

Chart.register(Legend, LinearScale, Tooltip, CategoryScale, BarElement);

const lastIndexWithValue = array => {
  const index = array
    .slice()
    .reverse()
    .findIndex(x => !!x);
  return index >= 0 ? array.length - 1 - index : -1;
};

const getDataSetColor = datasetIndex => (datasetIndex % 2 === 0 ? '#E0DBFD' : '#7564B6');
const getLabelColors = labels => labels.map((item, i) => (i % 2 === 0 ? '#E0DBFD' : '#7564B6'));
const getMonthLabel = (intl, month) => intl.formatMessage(listItemMsg('month', month));

const MonthlyRevenueDashboard = ({ intl, companyId }) => {
  const [showComparison, setShowComparison] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [totals, setTotals] = useState(null);

  const buildSingleYear = useCallback(() => {
    if (!rawData || !rawData.datasets) return;
    const [currentYearDataset] = rawData.datasets;
    if (!currentYearDataset) return;

    const lastIndex = lastIndexWithValue(currentYearDataset.values) + 1;

    const currentYearMonths = rawData.xLabels.slice(0, lastIndex);
    const chartXLabels = currentYearMonths.map(x => getMonthLabel(intl, parseInt(x)));
    const year = currentYearDataset.metadata;
    setTotals([{ year, total: currentYearDataset.total }]);
    setChartData({
      labels: chartXLabels,
      datasets: [
        {
          label: `${intl.formatMessage(messages.FiscalYear)} ${year}`,
          backgroundColor: getLabelColors(chartXLabels),
          borderColor: getLabelColors(chartXLabels),
          maxBarThickness: 100,
          data: currentYearDataset.values.slice(0, lastIndex),
        },
      ],
    });
  }, [rawData, intl]);
  const buildMultiYear = useCallback(() => {
    if (!rawData || !rawData.datasets) return;
    const { datasets = [], xLabels } = rawData;

    setTotals(datasets.map(({ total, metadata }) => ({ year: metadata, total })));
    setChartData({
      labels: xLabels.map(x => getMonthLabel(intl, parseInt(x))),
      datasets: datasets.map((dataset, idx) => ({
        label: `${intl.formatMessage(messages.FiscalYear)} ${dataset.metadata}`,
        backgroundColor: getDataSetColor(idx),
        borderColor: getDataSetColor(idx),
        maxBarThickness: 100,
        data: dataset.values,
      })),
    });
  }, [rawData, intl]);

  const toggleMode = () => {
    setShowComparison(!showComparison);
  };

  useEffect(() => {
    setRawData(null);
    setChartData(null);
    const cancelToken = Axios.CancelToken.source();

    api.base
      .get(`${config.appUrls.api}/api/Invoice/BilledPerMonth/`, {
        params: {
          companyId,
        },
        cancelToken: cancelToken.token,
      })
      .then(({ data }) => {
        setRawData(data || {});
      }, handleHttpError(intl));
    return () => {
      if (cancelToken) cancelToken.cancel('Operation canceled due to new request.');
    };
  }, [companyId, intl]);
  useEffect(() => {
    if (showComparison) {
      buildMultiYear();
    } else {
      buildSingleYear();
    }
  }, [rawData, showComparison, buildMultiYear, buildSingleYear]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: showComparison },
      },
      y: {
        ticks: {
          beginAtZero: true,
          callback: (value = 0) =>
            intl.formatNumber(value, {
              format: 'currency',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
        },
      },
    },
    interaction: {
      mode: 'index',
    },

    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        xPadding: 10,
        yPadding: 10,
        displayColors: false,
        callbacks: {
          label: context => currencyTooltipLabelWithInlt(context, intl),
        },
      },
    },
  };

  const loading = !rawData;
  const hasData = !!(chartData && chartData.datasets && chartData.datasets.length);

  return (
    <div className="dashboard-chart">
      <div className="title-block">
        <FormattedMessage
          {...(showComparison ? messages.BilledLast3FiscalYears : messages.BilledThisYear)}
        />
        {!!rawData && (
          <a
            className="right"
            title={intl.formatMessage(
              showComparison
                ? messages.SwitchToCurrentFiscalYear
                : messages.SwitchToComparisonFiscalYears,
            )}
            onClick={() => toggleMode()}
          >
            <i className="fa fa-bar-chart" />
          </a>
        )}
      </div>
      <div
        className={classnames('content-block nothing-to-bill', { nodata: !loading && !hasData })}
      >
        {loading && <LoadingAnimation />}
        {!loading && hasData && (
          <div className="chart-container">
            <Bar data={chartData} options={chartOptions} />
          </div>
        )}
      </div>
      {!loading && hasData && !!totals && (
        <div className="bottom-block">
          {totals.map(x => (
            <div className="margin5T" key={x.year}>
              <span className="text-uppercase ">
                <FormattedMessage {...messages.FiscalYear} /> {x.year}:{' '}
              </span>
              <span>
                <FormattedCurrency value={x.total || 0} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

MonthlyRevenueDashboard.propTypes = {
  intl: intlShape.isRequired,
  companyId: PropTypes.string.isRequired,
};

export default injectIntl(MonthlyRevenueDashboard);
